import React, { useCallback, useContext, useMemo } from 'react';
import { FlexColumn, Table } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';

import usePositions from '@ubeya/shared/hooks/account/usePositions';
import { useField } from 'react-final-form';
import styled from 'styled-components';
import Select from '@ubeya/shared-web/components/Select/Select';
import useUser from '@ubeya/shared/hooks/auth/useUser';
import PositionMappedToSupplierToggleRenderer from './PositionMappedToSupplierToggleRenderer';

const Wrapper = styled(FlexColumn)`
  flex: 1;
  padding: 31px 50px;
  overflow: auto;
`;

const Description = styled.div`
  margin-bottom: 15px;
  font-size: 16px;
  font-weight: 500;
`;

const Container = styled(FlexColumn)`
  flex: 1;
  min-width: 500px;
  .data-grid {
    margin-bottom: 25px;
  }
`;

const TogglesWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const ToggleTitle = styled.div`
  margin-right: 5px;
`;

const MappedWithSupplierHeader = () => {
  const { t } = useContext(ConfigContext);
  return (
    <>
      <TogglesWrapper>
        <ToggleTitle>{t('positionMappedWithSupplier')}</ToggleTitle>
      </TogglesWrapper>
    </>
  );
};

const SupplierPositionsMappingTab = ({ supplierPositions }) => {
  const { t } = useContext(ConfigContext);
  const { isUbeyaAdmin } = useUser();
  const { mappedPositions, filteredPositionsByAllowedBranchIds } = usePositions();

  const {
    input: { value: mappedClientPositionsToSupplierIds, onChange: onChangeMappedClientPositionsToSupplierIds }
  } = useField('mappedClientPositionsToSupplierIds');

  const {
    input: { value: manualClientSupplierPositionMapping, onChange: onChangeManualClientSupplierPositionMapping }
  } = useField('manualClientSupplierPositionMapping');

  const handleChangeMappedPositions = useCallback(
    (positionId, value) => {
      const mappedClientPositionsToSupplierUpdatedIds = value
        ? [...mappedClientPositionsToSupplierIds, positionId]
        : mappedClientPositionsToSupplierIds.filter((id) => id !== positionId);

      onChangeMappedClientPositionsToSupplierIds(mappedClientPositionsToSupplierUpdatedIds);
    },
    [mappedClientPositionsToSupplierIds, onChangeMappedClientPositionsToSupplierIds]
  );

  const groupedData = useMemo(
    () =>
      filteredPositionsByAllowedBranchIds.map(({ id }) => {
        const isClientPositionMappedToSupplier = mappedClientPositionsToSupplierIds.includes(id);

        return {
          positionId: id,
          isClientPositionMappedToSupplier
        };
      }),
    [filteredPositionsByAllowedBranchIds, mappedClientPositionsToSupplierIds]
  );

  const supplierPositionsOptions = (supplierPositions || []).map(({ id, slug }) => ({ value: id, label: t(slug) }));

  const columns = useMemo(
    () => [
      {
        Header: t('supplierPositionsMappingClientsPositions'),
        dataKey: 'positionId',
        formatter: (value) => mappedPositions[value]?.label
      },

      {
        Header: () => <MappedWithSupplierHeader />,
        dataKey: 'isClientPositionMappedToSupplier',
        Cell: PositionMappedToSupplierToggleRenderer,
        columnData: { onChange: handleChangeMappedPositions, readOnly: !isUbeyaAdmin },
        captureRowClick: false,
        minWidth: 400
      },

      isUbeyaAdmin && {
        Header: 'supplier positions (manual mapping)',
        dataKey: 'manualMapping',
        Cell: ({ rowData: { positionId: clientPositionId } }) => {
          return (
            <Select
              value={manualClientSupplierPositionMapping?.[clientPositionId] || null}
              options={supplierPositionsOptions}
              usePortal
              onChange={(val) =>
                onChangeManualClientSupplierPositionMapping({
                  ...manualClientSupplierPositionMapping,
                  [clientPositionId]: val
                })
              }
            />
          );
        },
        columnData: { onChange: handleChangeMappedPositions },
        captureRowClick: false,
        minWidth: 400
      }
    ],
    [
      t,
      handleChangeMappedPositions,
      isUbeyaAdmin,
      mappedPositions,
      manualClientSupplierPositionMapping,
      supplierPositionsOptions,
      onChangeManualClientSupplierPositionMapping
    ]
  );

  return (
    <Wrapper>
      <Description>{t('supplierPositionsMappingTabTitle')}</Description>

      <Container>
        <Table headerRowHeight={50} autoHeight columns={columns} data={groupedData} shadow={false} />
      </Container>
    </Wrapper>
  );
};

export default React.memo(SupplierPositionsMappingTab);
